/* eslint-disable import/no-cycle */
import { push } from 'connected-react-router';
import { createRoutine } from 'redux-saga-routines';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as authServices from 'api/auth';
import { removeAllPersistentData } from 'utils/localstorage';
import firebase from 'utils/auth/firebase';
import { parseFirebaseError } from 'utils/auth/errors';
import ROUTES, { getTabPageRoute } from 'utils/routes';
import { getConfig } from '../app';

export const signupRoutine = createRoutine('SIGNUP');
export const loginRoutine = createRoutine('LOGIN');
export const passwordResetRoutine = createRoutine('PASSWORD_RESET');
export const logoutRoutine = createRoutine('LOGOUT');
export const loginOAuthRoutine = createRoutine('LOGIN_OAUTH');
export const resetAuthErrorRoutine = createRoutine('RESET_AUTH_ERROR');
export const googleOauthRoutine = createRoutine('GOOGLE_OAUTH');
export const facebookOauthRoutine = createRoutine('FACEBOOK_OAUTH');
export const appleOauthRoutine = createRoutine('APPLE_OAUTH');

export const initialState = Object.freeze({
  isLoading: false,
  error: null,
  success: null,
});

export const authReducer = (state = initialState, action) => {
  switch (action.type) {
    case signupRoutine.REQUEST:
    case loginRoutine.REQUEST:
    case passwordResetRoutine.REQUEST:
    case logoutRoutine.REQUEST:
    case googleOauthRoutine.REQUEST:
    case facebookOauthRoutine.REQUEST:
    case appleOauthRoutine.REQUEST:
      return {
        ...state,
        error: null,
        success: null,
        isLoading: true,
      };
    case signupRoutine.SUCCESS:
    case loginRoutine.SUCCESS:
    case passwordResetRoutine.SUCCESS:
    case loginOAuthRoutine.SUCCESS:
    case googleOauthRoutine.SUCCESS:
    case facebookOauthRoutine.SUCCESS:
    case appleOauthRoutine.SUCCESS:
      return {
        ...state,
        error: null,
        isLoading: false,
        success: action.payload || null,
      };
    case signupRoutine.FAILURE:
    case loginRoutine.FAILURE:
    case passwordResetRoutine.FAILURE:
    case loginOAuthRoutine.FAILURE:
    case googleOauthRoutine.FAILURE:
    case facebookOauthRoutine.FAILURE:
    case appleOauthRoutine.FAILURE:
    case logoutRoutine.FAILURE:
      return {
        ...state,
        isLoading: false,
        success: null,
        error: action.payload,
      };
    case resetAuthErrorRoutine.REQUEST:
      return {
        ...initialState,
      };
    default:
      return { ...state };
  }
};

export const getUserId = ({ firebase }) => firebase?.auth?.uid;

export function* signupSaga({ payload: formValues }) {
  try {
    const userCredential = yield call(authServices.signUp, formValues);

    window.dataLayer.push({
      event: 'aud_signup',
      aud_user_id: userCredential.user.uid,
      aud_user_email: userCredential.user.email,
    });

    yield put(signupRoutine.success());

    const config = yield select(getConfig);
    const firstContentTab = config?.tabs?.[0]?.children?.[0] ?? config?.tabs?.[0];
    const routeToRedirect = getTabPageRoute(firstContentTab.target, firstContentTab.targetId);

    yield put(push(routeToRedirect));
  } catch (err) {
    const { message } = parseFirebaseError(err);

    yield put(signupRoutine.failure(message));
  }
}

export function* loginSaga({ payload: formValues }) {
  try {
    yield call(authServices.login, formValues);

    firebase.analytics().setUserId(firebase.auth().currentUser.uid);
    firebase.analytics().logEvent('login', { method: 'password' });

    yield put(loginRoutine.success());

    const config = yield select(getConfig);
    const firstContentTab = config?.tabs?.[0]?.children?.[0] ?? config?.tabs?.[0];
    const routeToRedirect = getTabPageRoute(firstContentTab.target, firstContentTab.targetId);

    yield put(push(routeToRedirect));
  } catch (err) {
    const { message } = parseFirebaseError(err);

    yield put(loginRoutine.failure(message));
  }
}

export function* passwordResetSaga({ payload: formValues }) {
  try {
    yield call(authServices.resetPassword, formValues);

    yield put(passwordResetRoutine.success('auth_firebase_alert_message_resetpasswordsuccess'));
  } catch (err) {
    const { message } = parseFirebaseError(err);

    yield put(passwordResetRoutine.failure(message));
  }
}

export function* logoutSaga() {
  try {
    yield call(authServices.logout);

    yield put(logoutRoutine.success());
    removeAllPersistentData();
    yield put(push(ROUTES.root));
  } catch (err) {
    const { message } = parseFirebaseError(err);

    yield put(logoutRoutine.failure(message));
  }
}

export function* loginOAuthSaga({ payload: token }) {
  try {
    yield call(authServices.loginWithCustomToken, token);

    firebase.analytics().setUserId(firebase.auth().currentUser.uid);
    firebase.analytics().logEvent('login', { method: 'custom_token' });

    yield put(loginOAuthRoutine.success());

    const config = yield select(getConfig);
    const firstContentTab = config?.tabs?.[0]?.children?.[0] ?? config?.tabs?.[0];
    const routeToRedirect = getTabPageRoute(firstContentTab.target, firstContentTab.targetId);

    yield put(push(routeToRedirect));
  } catch (err) {
    const { message } = parseFirebaseError(err);

    yield put(loginOAuthRoutine.failure(message));
  }
}

function* firebaseProviderOauthSaga(action, routine, method) {
  try {
    yield call(action);

    firebase.analytics().setUserId(firebase.auth().currentUser.uid);
    firebase.analytics().logEvent('login', { method });

    yield put(routine.success());

    const config = yield select(getConfig);
    const firstContentTab = config?.tabs?.[0]?.children?.[0] ?? config?.tabs?.[0];
    const routeToRedirect = getTabPageRoute(firstContentTab.target, firstContentTab.targetId);

    yield put(push(routeToRedirect));
  } catch (err) {
    const { message } = parseFirebaseError(err);
    yield put(routine.failure(message));
  }
}

const googleOauthSaga = () => firebaseProviderOauthSaga(authServices.loginWithGoogle, googleOauthRoutine, 'google');
const facebookOauthSaga = () =>
  firebaseProviderOauthSaga(authServices.loginWithFacebook, facebookOauthRoutine, 'facebook');
const appleOauthSaga = () => firebaseProviderOauthSaga(authServices.loginWithApple, appleOauthRoutine, 'apple');

export function* watchAuthSagas() {
  yield takeLatest(signupRoutine.REQUEST, signupSaga);
  yield takeLatest(loginRoutine.REQUEST, loginSaga);
  yield takeLatest(passwordResetRoutine.REQUEST, passwordResetSaga);
  yield takeLatest(logoutRoutine.REQUEST, logoutSaga);
  yield takeLatest(loginOAuthRoutine.REQUEST, loginOAuthSaga);
  yield takeLatest(googleOauthRoutine.REQUEST, googleOauthSaga);
  yield takeLatest(facebookOauthRoutine.REQUEST, facebookOauthSaga);
  yield takeLatest(appleOauthRoutine.REQUEST, appleOauthSaga);
}
